import type { ApolloClient, NormalizedCacheObject } from "@apollo/client";
import { NavigationContainer, DefaultTheme, useNavigation } from "@react-navigation/native";
import {
  createNativeStackNavigator,
  type NativeStackHeaderProps,
  type NativeStackNavigationOptions,
} from "@react-navigation/native-stack";
import React, { Suspense, use, useEffect, useMemo, useRef } from "react";
import { Platform, StyleSheet } from "react-native";

import { jobCardDataLocalTypePolicies } from "../../jobcard/typePolicies";
import { Box, ChevronLeft, Pressable, Modal } from "../../mads";
import { OneGraphApolloClientProvider } from "../../shared/apollo";
import apolloErrorLink from "../../shared/apollo/apolloErrorLink";
import { CrossPlatformRootTagProvider } from "../../shared/context/CrossPlatformRootTagContext";
import { normalizeEnvironment } from "../../shared/environment";
import { I18nProvider, useI18n, useLocale, type LocaleData } from "../../shared/I18n";
import { BridgeProvider } from "../../shared/native-modules/BridgeContext";
import fetchNativeOneGraphOperation from "../../shared/native-modules/core/fetchNativeOneGraphQuery";
import {
  getCookiesForViewJob,
  type ViewJobReadableCookies,
} from "../../shared/native-modules/core/getCookies";
import { awaitBridges } from "../../shared/native-modules/tare/awaitBridges";
import { TareDataProvider } from "../../shared/native-modules/tare/TareDataContext";
import type { ProctorAllocation } from "../../shared/native-modules/types/JSTProctorBridge";
import type {
  BridgeImplementations,
  TypedNativeModules,
} from "../../shared/native-modules/types/NativeModulesTypes";
import type {
  JSTAppSessionInfo,
  TareAccountStatus,
  TareAppModel,
  TareDeviceModel,
} from "../../shared/native-modules/types/TareBridge";
import { parsePrforceGroups } from "../../shared/proctor/parsePrforceGroups";
import { buildProctorBucketAllocation } from "../../shared/proctor/ProctorBucketAllocation";
import type { NativeContextRNProps } from "../../shared/props/NativeContextRNProps";
import { CookieProvider } from "../../shared/util/CookieProvider";
import eventEmitter from "../../shared/util/eventEmitter";
import { NavigationType } from "../../shared/util/NavigationType";
import { recordNpsViewjobTrigger } from "../../shared/util/npsViewjobTrigger";
import ScreenType, {
  isWebBottomSheet,
  ScreenTypeProvider,
  useIsWebBottomSheet,
} from "../../shared/util/ScreenType";
import { ViewjobProctorProvider } from "../proctor/__generated__/ViewjobProctorContext";
import {
  RnViewjobOnegraphTst,
  ViewjobProctorSpecifications,
} from "../proctor/__generated__/ViewjobProctorSpecifications";
import type { AppContentProps } from "./AppContent";
import { NavigationBar } from "./components/core/JobComponent/NavigationBar/NavigationBar";
import { JMUIModalContextProvider } from "./components/core/ProfileInsights/context/JMUIModalContextProvider";
import {
  isDesktopDisplay as isDesktopDisplaySurface,
  type RnViewJobDisplay,
  ViewJobDisplayProvider,
} from "./context/ViewJobDisplayContext";
import { getPlacementIdFromUrl } from "./hooks/getJobDataInput";
import { shouldUseOneGraphDataPath } from "./lib/shouldUseOneGraphDataPath";
import type { RootStackParamList } from "./navigation/types";
import { getPlatformOptions } from "./PlatformOptions";
import { extractLocale } from "./util/extractLocale";
import { useGetApolloDefaultOptions } from "./util/useGetApolloDefaultOptions";

// default theme has a different background, override it to white
const CustomBackgroundTheme = {
  ...DefaultTheme,
  colors: {
    ...DefaultTheme.colors,
    background: "white",
  },
};

const Stack = createNativeStackNavigator<RootStackParamList>();

const importAppContent = () =>
  import(
    /* webpackChunkName: "app-content-rest" */
    "./AppContent"
  );

const importAppContentGraphQL = () =>
  import(
    /* webpackChunkName: "app-content-onegraph" */
    "./AppContentGraphQL"
  );

const AppContentLazy = React.lazy(() =>
  importAppContent().then((module) => ({ default: module.AppContent })),
);

const AppContentGraphQLLazy = React.lazy(() =>
  importAppContentGraphQL().then((module) => ({ default: module.AppContentGraphQL })),
);

/**
 * Resolves the content chunk a hydrating client is about to render.
 *
 * React throws away the server HTML of any Suspense boundary whose children
 * suspend during hydration, so reaching `AppContentLazy` / `AppContentGraphQLLazy`
 * with their chunk still unfetched blanks the SSR'd job for a full chunk round
 * trip. Web hydration entries await this before `hydrateRoot` and pass the
 * result back in as `restComponent` / `oneGraphComponent`. Both loaders reuse
 * the `React.lazy` call sites' import specifiers and chunk names, so this
 * shares their chunk instead of emitting a duplicate.
 */
export async function loadAppContentComponent(
  useOneGraph: boolean,
): Promise<React.ComponentType<AppContentProps>> {
  if (useOneGraph) {
    return (await importAppContentGraphQL()).AppContentGraphQL;
  }
  return (await importAppContent()).AppContent;
}

const CommutePreferenceModal = React.lazy(() =>
  import(
    /* webpackChunkName: "commute-preference-modal" */
    "./components/core/JobComponent/JobSummaryTest/CommuteSection/CommutePreferenceModal"
  ).then((module) => ({ default: module.CommutePreferenceModal })),
);

const ReportContentModal = React.lazy(() =>
  import(
    /* webpackChunkName: "report-content-modal" */
    "./components/core/ReportContent/ReportContent/ReportContentModal"
  ).then((module) => ({ default: module.ReportContentModal })),
);

const CollectionModal = React.lazy(() =>
  import(
    /* webpackChunkName: "collection-modal" */
    "./components/core/ProfileInsights/components/common/CollectionModal"
  ).then((module) => ({ default: module.CollectionModal })),
);

const CollectionModalHeader = React.lazy(() =>
  import(
    /* webpackChunkName: "collection-modal-header" */
    "./components/core/ProfileInsights/components/common/CollectionModal/CollectionModalHeader"
  ).then((module) => ({ default: module.CollectionModalHeader })),
);

const AddQualificationsNavigator = React.lazy(() =>
  import(
    /* webpackChunkName: "add-qualifications" */
    "./components/core/ProfileInsights/components/MatchOverviewQualificationCollection/AddQualifications/AddQualificationsNavigator"
  ).then((module) => ({ default: module.AddQualificationsNavigator })),
);

const ReportContentHeader = React.lazy(() =>
  import(
    /* webpackChunkName: "report-content-header" */
    "./components/core/ReportContent/ReportContent/ReportContentHeader"
  ).then((module) => ({ default: module.ReportContentHeader })),
);

const AIDisclaimerModal = React.lazy(() =>
  import(
    /* webpackChunkName: "ai-disclaimer-modal" */
    "./components/core/JobComponent/JobSummaryTest/AIDisclaimerModal"
  ).then((module) => ({ default: module.AIDisclaimerModal })),
);

const CompanySummaryDisclaimerModal = React.lazy(() =>
  import(
    /* webpackChunkName: "company-summary-disclaimer-modal" */
    "./components/core/JobComponent/EmployerInsights/CompanySummaryDisclaimerModal"
  ).then((module) => ({ default: module.CompanySummaryDisclaimerModal })),
);

const ProfileWebViewScreen = React.lazy(() =>
  import(
    /* webpackChunkName: "profile-webview-screen" */
    "./components/core/ProfileWebViewScreen"
  ).then((module) => ({ default: module.ProfileWebViewScreen })),
);

function renderReportContentHeader(_props: NativeStackHeaderProps) {
  return (
    <Suspense fallback={null}>
      <ReportContentHeader />
    </Suspense>
  );
}

/**
 * Provides a single Apollo client to every stack screen so mutations fired
 * from modal screens propagate to the parent screen's queries via Apollo's
 * normalized cache.
 */
function SharedApolloProvider({
  children,
  nativeContext,
  prforceGroups,
  url,
  apolloClient,
  restoreWindowApolloState,
}: {
  children: React.ReactNode;
  nativeContext?: NativeContextRNProps;
  prforceGroups?: string | null;
  url?: string | null;
  apolloClient?: ApolloClient<NormalizedCacheObject>;
  restoreWindowApolloState?: boolean;
}) {
  const locale = useLocale();
  const errorLink = useMemo(
    () => apolloErrorLink({ provider: "ViewJob", nativeContext }),
    [nativeContext],
  );
  const defaultOptions = useGetApolloDefaultOptions({ ctk: nativeContext?.ctk, locale });
  const platformOptions = useMemo(() => getPlatformOptions(), []);
  const placementId = useMemo(
    () => (url ? (getPlacementIdFromUrl(url) ?? undefined) : undefined),
    [url],
  );

  return (
    <OneGraphApolloClientProvider
      fetch={fetchNativeOneGraphOperation}
      defaultOptions={defaultOptions}
      platformOptions={platformOptions}
      prforceGroups={prforceGroups ?? undefined}
      errorLink={errorLink}
      placementId={placementId}
      typePolicies={jobCardDataLocalTypePolicies}
      externalClient={apolloClient}
      restoreWindowApolloState={restoreWindowApolloState}
    >
      {children}
    </OneGraphApolloClientProvider>
  );
}

/**
 * Shared header back button (mads chevron + localized "Back" a11y label) used by the
 * pushed/modal View Job screens, so the back affordance matches the rest of View Job
 * instead of the native-stack default (which leaks the previous route name).
 */
export function HeaderBackButton() {
  const gt = useI18n();
  const navigation = useNavigation();

  return (
    <Pressable
      sx={backButtonStyles.button}
      onPress={() => navigation.goBack()}
      accessible={true}
      accessibilityRole="button"
      accessibilityLabel={gt.gettext("Back")}
    >
      <ChevronLeft fill="black" size="md" />
    </Pressable>
  );
}

// Module-level so it's a stable reference (avoids react/no-unstable-nested-components
// and matches the renderReportContentHeader pattern used by the other screens).
function renderHeaderBackButton() {
  return <HeaderBackButton />;
}

const backButtonStyles = StyleSheet.create({
  button: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    marginLeft: 4,
  },
});

const reportContentWrapperStyles = StyleSheet.create({
  mobileContainer: {
    flex: 1,
    backgroundColor: "white",
    width: "100%",
    bottom: 0,
    top: 32,
    overflow: "hidden",
    borderTopLeftRadius: 12,
    borderTopRightRadius: 12,
    paddingBottom: 16,
    position: "fixed",
  },
  modalContent: {
    backgroundColor: "white",
    width: 600,
    borderRadius: 20,
    padding: 12,
  },
  mobileBackground: {
    backgroundColor: "rgba(0,0,0,0.4)",
    width: "100%",
    height: "100%",
  },
  modalBackground: {
    backgroundColor: "rgba(0,0,0,0.4)",
    position: "absolute",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
});

/**
 * Wraps ReportContentModal in an extra container when on web (e.g. for layout or styling).
 * On native, renders ReportContentModal directly.
 */
function ReportContentScreenWrapper({
  isDesktop = false,
  ...props
}: React.ComponentProps<typeof ReportContentModal> & { isDesktop?: boolean }) {
  const isWebBottomSheet = useIsWebBottomSheet();
  const reportContent = (
    <Suspense fallback={null}>
      <ReportContentModal {...props} />
    </Suspense>
  );
  const reportContentHeader = (
    <Suspense fallback={null}>
      <ReportContentHeader />
    </Suspense>
  );
  if (isWebBottomSheet) {
    return (
      <Box sx={reportContentWrapperStyles.mobileBackground}>
        <Box sx={reportContentWrapperStyles.mobileContainer}>
          {reportContentHeader}
          {reportContent}
        </Box>
      </Box>
    );
  }
  if (isDesktop) {
    return (
      <Modal animationType="fade" visible={true} transparent={true}>
        <Box sx={reportContentWrapperStyles.modalBackground}>
          <Box sx={reportContentWrapperStyles.modalContent}>
            {reportContentHeader}
            {reportContent}
          </Box>
        </Box>
      </Modal>
    );
  }
  return <>{reportContent}</>;
}

/**
 * Returns platform-specific navigation options for the CollectionModal screen.
 * iOS uses NavigationBar component, Android uses CollectionModalHeader with custom back button for accessibility.
 */
const getCollectionModalOptions = () => {
  if (Platform.OS === "ios") {
    return () => ({
      headerShown: true,
      presentation: "modal" as const,
      header: (props: NativeStackHeaderProps) => (
        <NavigationBar
          url={""}
          showSaveJobButton={false}
          showShareButton={false}
          isLoggedIn={false}
          reload={() => {}}
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
          title={(props.route.params as { title?: string })?.title ?? ""}
          showBorder={true}
          renderedOn="CollectionModal"
        />
      ),
    });
  }

  return (): NativeStackNavigationOptions => ({
    headerShown: true,
    presentation: "modal" as const,
    headerTitle: () => (
      <Suspense fallback={null}>
        <CollectionModalHeader />
      </Suspense>
    ),
    headerLeft: renderHeaderBackButton,
  });
};

export type ViewJobBridgeDependencies = {
  RNTareBridge: TypedNativeModules["RNTareBridge"];
  RNCoreNativeModule: TypedNativeModules["RNCoreNativeModule"];
  ViewJobModule: TypedNativeModules["ViewJobModule"];
  proctorAllocations: Record<string, ProctorAllocation>;
  appModel: TareAppModel;
  accountStatus: TareAccountStatus;
  deviceModel: TareDeviceModel;
  appSessionInfo: JSTAppSessionInfo | null;
  viewjobCookies: Record<ViewJobReadableCookies, string | null>;
};

/**
 * The type definition for the entrypoint of the app.
 *
 * Partial marks every field as potentially undefined because
 * old app versions will not provide newly introduced fields
 *
 * We omit 'getVJData' because it is used as a prop to provide
 * mock data. The real implementation is provided through the
 * `NativeModules` integration.
 *
 * We add a locale prop because old versions of the app do not
 * have access to TARE.
 */
export type AppProps = Partial<
  Omit<AppContentProps, "getVJData"> & {
    locale: string;
    navigationType?: NavigationType | null;
    /**
     * Host display surface. On the OneGraph path there is no preloaded
     * model to read `rnViewJobDisplay` from, so the host supplies it here.
     */
    viewJobDisplay?: RnViewJobDisplay;
    /**
     * Web only: the caller's explicit signal to use the OneGraph data path.
     * When set, this wins over Apollo SSR detection (`apolloClient` /
     * `window.__APOLLO_STATE__`). The OneGraph embed (`mountViewJobWithContext`)
     * sets this by default; pass `forceOneGraph: false` to fetch via the servlet
     * REST/`spa=1` path instead. The legacy `mountViewJob` embed and standalone
     * web entries leave it unset and stay on REST unless the page is Apollo-SSR'd.
     * Ignored on native, which gates on rn_viewjob_onegraph_tst.
     */
    forceOneGraph?: boolean;
    // The Apollo SSR render entry resolves bridge dependencies before rendering
    // and passes the resolved value; the app/native paths pass a Promise.
    bridgeDependencies: Promise<ViewJobBridgeDependencies> | ViewJobBridgeDependencies;
    viewJobSource?: "ViewJob" | "ViewJobFeed";
    onRendered?: () => void;
    apolloClient?: ApolloClient<NormalizedCacheObject>;
    /**
     * Web SSR-injected gettext catalog for the active locale. When present it's
     * passed to I18nProvider so the viewjob web client bundle can drop the
     * bundled all-locales catalog (RN-6851 / DISCO-5470). Unset on native and
     * other paths, which fall back to the bundled catalog.
     */
    catalog?: LocaleData;
    /**
     * Pre-resolved content component overrides, one per data path. The server
     * render entry injects `oneGraphComponent` so `renderToString` has a
     * synchronous component; the web client entry injects whichever path it is
     * about to hydrate so the content boundary can't suspend mid-hydration and
     * discard the SSR markup (see {@link loadAppContentComponent}). Entries
     * that don't hydrate leave both unset and keep the lazy chunks for RN-9111
     * bundle-size behavior.
     */
    oneGraphComponent?: React.ComponentType<AppContentProps>;
    restComponent?: React.ComponentType<AppContentProps>;
  }
>;

export async function createViewJobBridgeDependencies(
  bridges: BridgeImplementations<"RNCoreNativeModule" | "RNTareBridge" | "ViewJobModule">,
): Promise<ViewJobBridgeDependencies> {
  const [
    RNTareBridge,
    RNCoreNativeModule,
    ViewJobModule,
    appModel,
    accountStatus,
    deviceModel,
    appSessionInfo,
    proctorAllocations,
    viewjobCookies,
  ] = await Promise.all([
    bridges.RNTareBridge,
    bridges.RNCoreNativeModule,
    bridges.ViewJobModule,
    bridges.RNTareBridge.then((tareApi) => tareApi.getAppModel()),
    bridges.RNTareBridge.then((tareApi) => tareApi.getAccountStatus()),
    bridges.RNTareBridge.then((tareApi) => tareApi.getDeviceModel()),
    bridges.RNTareBridge.then((tareApi) => tareApi.getAppSessionInfo()),
    bridges.RNTareBridge.then((tareApi) =>
      tareApi.getProctorAllocations(ViewjobProctorSpecifications.map((t) => t.name)),
    ),
    bridges.RNCoreNativeModule.then((coreApi) => getCookiesForViewJob(coreApi)),
  ]);

  // The native bridge returns the raw `buildEnvironment` enum, which is
  // uppercase on Android (`PROD`/`QA`/`E2EQA`) and lowercase on iOS.
  // Canonicalize at the bridge boundary so consumers reading
  // `tareData.appModel?.buildEnvironment` see the same shape on both
  // platforms (RN-3079).
  const normalizedAppModel: TareAppModel = {
    ...appModel,
    buildEnvironment: normalizeEnvironment(appModel.buildEnvironment),
  };

  return {
    RNTareBridge,
    RNCoreNativeModule,
    ViewJobModule,
    appModel: normalizedAppModel,
    accountStatus,
    deviceModel,
    appSessionInfo,
    proctorAllocations,
    viewjobCookies,
  };
}

function App({
  url,
  trigger,
  source,
  nativeContext,
  loggingUrl,
  prforceGroups,
  renderStarts,
  navigationType,
  locale: localeProp,
  preloadedVJData,
  viewJobDisplay,
  forceOneGraph,
  bridgeDependencies,
  viewJobSource = "ViewJob",
  onRendered,
  apolloClient,
  catalog,
  oneGraphComponent,
  restComponent,
}: AppProps) {
  // `bridgeDependencies` may be a Promise (app / native / client paths) or an
  // already-resolved value (the Apollo SSR render entry awaits it before rendering).
  // Unwrap only when it is actually a thenable so a resolved value isn't mis-`use`d.
  const deps =
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
    typeof (bridgeDependencies as { then?: unknown })?.then === "function"
      ? // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
        use(bridgeDependencies as Promise<ViewJobBridgeDependencies>)
      : // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
        (bridgeDependencies as ViewJobBridgeDependencies);
  const {
    RNTareBridge: tareApi,
    RNCoreNativeModule: coreApi,
    ViewJobModule: viewjobApi,
    appModel,
    accountStatus,
    deviceModel,
    appSessionInfo,
    proctorAllocations,
    viewjobCookies,
  } = deps;

  const oneGraphAllocation = buildProctorBucketAllocation(
    RnViewjobOnegraphTst,
    proctorAllocations[RnViewjobOnegraphTst.name],
  );

  // This is part of the rendered output,
  // so it must evaluate identically on the server and the hydrating client. The server
  // receives the injected `apolloClient` (passed only by renderWithApolloState); the
  // browser receives the serialized `window.__APOLLO_STATE__` (injected only for
  // Apollo-SSR'd pages). Both are present iff this page came from the Apollo SSR entry,
  // so server and client agree and hydration matches. On web, an explicit
  // `forceOneGraph` wins over that detection so a REST embed can opt out even if
  // Apollo state is present on the page. Native ignores the prop and gates on
  // rn_viewjob_onegraph_tst (buckets 2 and 3).
  const isApolloDriven =
    apolloClient != null || (typeof window !== "undefined" && window.__APOLLO_STATE__ != null);

  const shouldCallOneGraph = shouldUseOneGraphDataPath({
    platformOS: Platform.OS,
    forceOneGraph,
    isApolloDriven,
    oneGraphAllocationValue: oneGraphAllocation.value,
  });

  // Resolve the host display surface once. Prefer the explicit prop (OneGraph
  // path); fall back to a preloaded model's value (REST / SSR path).
  const resolvedViewJobDisplay: RnViewJobDisplay =
    viewJobDisplay ??
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
    (preloadedVJData?.rnViewJobDisplay as RnViewJobDisplay | undefined) ??
    "MOBILE_STANDALONE";
  const isDesktopDisplay = isDesktopDisplaySurface(resolvedViewJobDisplay);
  const screenType =
    navigationType === NavigationType.PRESENT_SHEET
      ? isDesktopDisplay
        ? ScreenType.EMBEDDED_PANE
        : ScreenType.BOTTOM_SHEET
      : ScreenType.FULL_SCREEN;

  const locale = extractLocale(appModel, localeProp);

  const App = shouldCallOneGraph
    ? (oneGraphComponent ?? AppContentGraphQLLazy)
    : (restComponent ?? AppContentLazy);

  return (
    <ViewJobDisplayProvider display={resolvedViewJobDisplay}>
      <CrossPlatformRootTagProvider>
        <BridgeProvider
          implementations={{
            RNTareBridge: tareApi,
            RNCoreNativeModule: coreApi,
            ViewJobModule: viewjobApi,
          }}
        >
          <TareDataProvider tareData={{ appModel, deviceModel, accountStatus, appSessionInfo }}>
            <I18nProvider locale={locale} catalog={catalog}>
              <ScreenTypeProvider screenType={screenType}>
                <ViewjobProctorProvider
                  allocations={proctorAllocations}
                  override={parsePrforceGroups(nativeContext?.prforceGroups ?? "")}
                >
                  <CookieProvider<ViewJobReadableCookies> cookies={viewjobCookies}>
                    <SharedApolloProvider
                      nativeContext={nativeContext}
                      prforceGroups={prforceGroups}
                      url={url}
                      apolloClient={apolloClient}
                      restoreWindowApolloState={shouldCallOneGraph}
                    >
                      <NavigationContainer
                        onReady={onRendered}
                        theme={CustomBackgroundTheme}
                        documentTitle={
                          Platform.OS === "web"
                            ? {
                                enabled: false,
                              }
                            : undefined
                        }
                      >
                        <JMUIModalContextProvider>
                          <Stack.Navigator screenOptions={{ headerShown: false }}>
                            <Stack.Screen name="ViewJob">
                              {() => (
                                <Suspense fallback={null}>
                                  <App
                                    url={url ?? null}
                                    loggingUrl={loggingUrl ?? null}
                                    getVJData={viewjobApi.getVJData}
                                    prforceGroups={prforceGroups ?? null}
                                    trigger={trigger}
                                    source={source}
                                    nativeContext={nativeContext}
                                    renderStarts={renderStarts}
                                    preloadedVJData={preloadedVJData}
                                    viewJobSource={viewJobSource}
                                    apolloClient={apolloClient}
                                  />
                                </Suspense>
                              )}
                            </Stack.Screen>
                            <Stack.Screen
                              name="ReportContent"
                              options={() => ({
                                headerShown: !isWebBottomSheet(screenType) && !isDesktopDisplay,
                                presentation: isDesktopDisplay ? "transparentModal" : "modal",
                                header: renderReportContentHeader,
                              })}
                            >
                              {(props) => (
                                <ReportContentScreenWrapper
                                  isDesktop={isDesktopDisplay}
                                  {...props}
                                />
                              )}
                            </Stack.Screen>
                            <Stack.Screen
                              name="CollectionModal"
                              options={getCollectionModalOptions()}
                            >
                              {(props) => (
                                <Suspense fallback={null}>
                                  <CollectionModal {...props} />
                                </Suspense>
                              )}
                            </Stack.Screen>
                            <Stack.Screen
                              name="CommutePreferenceModal"
                              options={
                                isDesktopDisplay
                                  ? {
                                      headerShown: false,
                                      presentation: "transparentModal",
                                    }
                                  : {
                                      headerShown: false,
                                      presentation: "formSheet",
                                      sheetAllowedDetents: [0.6],
                                      sheetCornerRadius: 20,
                                      gestureEnabled: true,
                                    }
                              }
                            >
                              {() => (
                                <Suspense fallback={null}>
                                  <CommutePreferenceModal isDesktop={isDesktopDisplay} />
                                </Suspense>
                              )}
                            </Stack.Screen>
                            <Stack.Screen
                              name="AIDisclaimerModal"
                              options={{
                                headerShown: false,
                                presentation: "formSheet",
                                sheetAllowedDetents: [0.225],
                                sheetCornerRadius: 16,
                                gestureEnabled: false,
                              }}
                            >
                              {() => (
                                <Suspense fallback={null}>
                                  <AIDisclaimerModal />
                                </Suspense>
                              )}
                            </Stack.Screen>
                            <Stack.Screen
                              name="CompanySummaryDisclaimerModal"
                              options={{
                                headerShown: false,
                                presentation: "formSheet",
                                sheetAllowedDetents: [0.225],
                                sheetCornerRadius: 16,
                                gestureEnabled: false,
                              }}
                            >
                              {() => (
                                <Suspense fallback={null}>
                                  <CompanySummaryDisclaimerModal />
                                </Suspense>
                              )}
                            </Stack.Screen>
                            <Stack.Screen
                              name="AddQualifications"
                              options={{
                                // Native modal on mobile. Desktop web uses a
                                // transparent stack screen so the navigator
                                // can render its own centered dialog (RN-5815).
                                // Onboarding is a screen inside this modal so
                                // it can slide over the qualifications view.
                                // Keep the sheet from dismissing on drag; close
                                // is explicit via the header.
                                headerShown: false,
                                gestureEnabled: false,
                                presentation: isDesktopDisplay ? "transparentModal" : "modal",
                              }}
                            >
                              {(props) => (
                                <Suspense fallback={null}>
                                  <AddQualificationsNavigator {...props} />
                                </Suspense>
                              )}
                            </Stack.Screen>
                            <Stack.Screen
                              name="ProfileWebView"
                              options={{
                                headerShown: true,
                                // Match the back-button convention used by the
                                // other VJ header screens (mads chevron +
                                // localized "Back" label) instead of the
                                // native-stack default, which would show the
                                // previous route name ("ViewJob").
                                headerLeft: renderHeaderBackButton,
                              }}
                            >
                              {(props) => (
                                <Suspense fallback={null}>
                                  <ProfileWebViewScreen {...props} />
                                </Suspense>
                              )}
                            </Stack.Screen>
                          </Stack.Navigator>
                        </JMUIModalContextProvider>
                      </NavigationContainer>
                    </SharedApolloProvider>
                  </CookieProvider>
                </ViewjobProctorProvider>
              </ScreenTypeProvider>
            </I18nProvider>
          </TareDataProvider>
        </BridgeProvider>
      </CrossPlatformRootTagProvider>
    </ViewJobDisplayProvider>
  );
}

/**
 * Entry point for ViewJob.
 * - RNW (React Native Web): bridgeDependencies is initialized by the parent in web/viewjob/index.server.ts
 * - RN (React Native): bridgeDependencies is created here on each mount to ensure fresh cookies
 */
export const AppWithSuspenseBoundary: React.FC<AppProps> = (props) => {
  const renderStarts = useRef(Date.now());
  const bridgeDependencies = useMemo(
    () =>
      props.bridgeDependencies ??
      createViewJobBridgeDependencies(
        awaitBridges(["RNTareBridge", "RNCoreNativeModule", "ViewJobModule"]),
      ),
    [props.bridgeDependencies],
  );

  useEffect(() => {
    eventEmitter.emit("viewJobVisibilityChanged", { isVisible: true });
    // DISCO-5522: signal a View Job open so the RN Homepage or SERP can fire NPS
    // on the next return. Skip the ViewJobFeed, which mounts this component per
    // card rather than as a discrete user-initiated open. Best-effort and gated
    // behind jsj_nps_after_viewjob_tst by the reader.
    if (props.viewJobSource !== "ViewJobFeed") {
      void recordNpsViewjobTrigger();
    }
    return () => {
      eventEmitter.emit("viewJobVisibilityChanged", { isVisible: false });
    };
  }, [props.viewJobSource]);

  return (
    <Suspense>
      <App {...props} renderStarts={renderStarts.current} bridgeDependencies={bridgeDependencies} />
    </Suspense>
  );
};

export default AppWithSuspenseBoundary;
